import { defineMessages } from "react-intl";

import { EntityType } from "@mapmycustomers/shared";
import FilterOperator from "@mapmycustomers/shared/enum/FilterOperator";

import {
  checkEmailMenuItem,
  checkForCompaniesWithEmail,
  closeComposeModal,
} from "@app/component/flows/SendingEmails/common";
import TourDescriptor from "@app/component/flows/types/TourDescriptor";
import waitForElement from "@app/component/flows/utils/waitForElement";
import { getApiService } from "@app/store/api";
import { showEntityView } from "@app/store/entityView/actions";
import { showGlobalSearch } from "@app/store/globalSearch/actions";
import { getOrganizationId } from "@app/store/iam";
import CommonFieldName from "@app/util/fieldModel/CommonFieldName";
import companyFieldModel from "@app/util/fieldModel/CompanyFieldModel";
import { createAndCondition, createSimpleCondition } from "@app/util/filters/utils";
import { convertToPlatformFilterModel } from "@app/util/viewModel/convertToPlatformFilterModel";

const messages = defineMessages({
  step01a: {
    id: "guides.sendingEmailsTemplates.tour.step01a",
    defaultMessage: "Creating & Using Email Templates",
  },
  step01aDescription: {
    id: "guides.sendingEmailsTemplates.tour.step01a.description",
    defaultMessage:
      "Templates save time and ensure consistency when sending similar emails repeatedly.{br}{br}Let's proceed to the Compose Email modal real quick starting with Record selection. Feel free to select any record with an email.",
  },
  step01b: {
    id: "guides.sendingEmailsTemplates.tour.step01b",
    defaultMessage: "Creating & Using Email Templates",
  },
  step01bDescription: {
    id: "guides.sendingEmailsTemplates.tour.step01b.description",
    defaultMessage:
      "Templates save time and ensure consistency when sending similar emails repeatedly.{br}{br}Let's proceed to the Compose Email modal real quick, here we selected a random record with an email.",
  },
  step02: {
    id: "guides.sendingEmailsTemplates.tour.step02",
    defaultMessage: "Menu",
  },
  step02Description: {
    id: "guides.sendingEmailsTemplates.tour.step02.description",
    defaultMessage: "Open the menu by clicking on triple dots item",
  },
  step03: {
    id: "guides.sendingEmailsTemplates.tour.step03",
    defaultMessage: "Email",
  },
  step03Description: {
    id: "guides.sendingEmailsTemplates.tour.step03.description",
    defaultMessage: "Click the Email submenu.",
  },
  step04: {
    id: "guides.sendingEmailsTemplates.tour.step04",
    defaultMessage: "Email Button",
  },
  step04Description: {
    id: "guides.sendingEmailsTemplates.tour.step04.description",
    defaultMessage: "Select Send via MMC",
  },
  step05: {
    id: "guides.sendingEmailsTemplates.tour.step05",
    defaultMessage: "",
  },
  step05Description: {
    id: "guides.sendingEmailsTemplates.tour.step05.description",
    defaultMessage: "",
  },
  step06: {
    id: "guides.sendingEmailsTemplates.tour.step06",
    defaultMessage: "No emails",
  },
  step06Description: {
    id: "guides.sendingEmailsTemplates.tour.step06.description",
    defaultMessage:
      "In case some selected records have no emails, this modal will show up. Click Continue to proceed.",
  },
  step07: {
    id: "guides.sendingEmailsTemplates.tour.step07",
    defaultMessage: "Record type",
  },
  step07Description: {
    id: "guides.sendingEmailsTemplates.tour.step07.description",
    defaultMessage:
      "Select who you want to email in the modal that pops up.{br}{br}Pick either option and click Continue.",
  },
  step08: {
    id: "guides.sendingEmailsTemplates.tour.step08",
    defaultMessage: "Compose your email",
  },
  step08Description: {
    id: "guides.sendingEmailsTemplates.tour.step08.description",
    defaultMessage: "Now please add anything in the Subject line...",
  },
  step09: {
    id: "guides.sendingEmailsTemplates.tour.step09",
    defaultMessage: "Compose your email",
  },
  step09Description: {
    id: "guides.sendingEmailsTemplates.tour.step09.description",
    defaultMessage: "...and write anything in email body.",
  },
  step10: {
    id: "guides.sendingEmailsTemplates.tour.step10",
    defaultMessage: "Compose your email",
  },
  step10Description: {
    id: "guides.sendingEmailsTemplates.tour.step10.description",
    defaultMessage: "Now let's click this button.",
  },
  step11: {
    id: "guides.sendingEmailsTemplates.tour.step11",
    defaultMessage: "Create new template",
  },
  step11Description: {
    id: "guides.sendingEmailsTemplates.tour.step11.description",
    defaultMessage: "Click Save New",
  },
  step12: {
    id: "guides.sendingEmailsTemplates.tour.step12",
    defaultMessage: "Create new template",
  },
  step12Description: {
    id: "guides.sendingEmailsTemplates.tour.step12.description",
    defaultMessage: "Name your template clearly and save it.",
  },
  step13: {
    id: "guides.sendingEmailsTemplates.tour.step13",
    defaultMessage: "Template location",
  },
  step13Description: {
    id: "guides.sendingEmailsTemplates.tour.step13.description",
    defaultMessage:
      "Your new template appears in this dropdown. When creating a new email, you can pick any template you want to avoid starting from scratch.",
  },
  step14: {
    id: "guides.sendingEmailsTemplates.tour.step14",
    defaultMessage: "Template editing",
  },
  step14Description: {
    id: "guides.sendingEmailsTemplates.tour.step14.description",
    defaultMessage:
      "Now let's change anything, for example the subject. But you could also change email body as well.",
  },
  step15: {
    id: "guides.sendingEmailsTemplates.tour.step15",
    defaultMessage: "Template editing",
  },
  step15Description: {
    id: "guides.sendingEmailsTemplates.tour.step15.description",
    defaultMessage:
      "Click Update next to the template name to save changes. This is how you can keep your template up to date.",
  },
  step16: {
    id: "guides.sendingEmailsViaRecordPreview.tour.step16",
    defaultMessage: "Template editing",
  },
  step16Description: {
    id: "guides.sendingEmailsViaRecordPreview.tour.step16.description",
    defaultMessage: "Click Yes to confirm saving.",
  },
  step17: {
    id: "guides.sendingEmailsViaRecordPreview.tour.step17",
    defaultMessage: "Tour Complete",
  },
  step17Description: {
    id: "guides.sendingEmailsViaRecordPreview.tour.step17.description",
    defaultMessage: "🎉 Congratulations! You've completed this tour.",
  },
});

export const emailTemplatesTourConfiguration: TourDescriptor = {
  id: "mmc-sending-emails-via-record-preview",
  steps: [
    {
      id: "start",
      onBefore: async ({ store }) => {
        const result = await checkForCompaniesWithEmail(store);
        if (result) {
          return result;
        }

        const filterModel = createAndCondition([
          createSimpleCondition(CommonFieldName.EMAIL, FilterOperator.NOT_EMPTY),
        ]);

        const apiService = getApiService(store.getState());
        const orgId = getOrganizationId(store.getState());
        const response = await apiService!.fetchCompanies(orgId!, {
          $columns: ["id"],
          $filters: convertToPlatformFilterModel(filterModel, [], companyFieldModel),
          $limit: 1,
        });

        if (response.data.length) {
          store.dispatch(
            showEntityView({ entityId: response.data[0].id, entityType: EntityType.COMPANY })
          );
          return { action: "navigate", stepId: "introB" };
        } else {
          store.dispatch(showGlobalSearch());
          return { action: "navigate", stepId: "introA" };
        }
      },
    },
    {
      id: "introA",
      description: messages.step01aDescription,
      key: "intro",
      title: messages.step01a,
      waitForEvent: {
        eventType: "internal",
        selector: "Clicked: Universal Search -> Clicked row",
      },
    },
    {
      id: "introB",
      description: messages.step01bDescription,
      key: "intro",
      title: messages.step01b,
    },
    {
      id: "menu",
      description: messages.step02Description,
      options: {
        disableInteraction: false,
        showBackground: true,
        showButtons: false,
      },
      position: "left",
      selector: "[class*=RecordActionsBar_container] [data-item-id=ellipsis]",
      title: messages.step02,
      waitForEvent: {
        eventType: "click",
        selector: "[class*=RecordActionsBar_container] [data-item-id=ellipsis]",
      },
    },
    {
      id: "email-button-1",
      description: messages.step03Description,
      onBefore: async () => {
        // just letting the dropdown animation to complete
        await new Promise((resolve) => setTimeout(resolve, 100));

        const result = await checkEmailMenuItem();
        if (result) {
          // close drawer
          document
            .querySelector<HTMLElement>("[class*=RecordPane_container] .ant-drawer-close")
            ?.click();

          return result;
        }
      },
      options: {
        disableInteraction: false,
        showBackground: true,
        showButtons: false,
      },
      position: "left",
      selector: ".ant-dropdown-menu-submenu[data-item-id=email]",
      selectorFoundDelay: 100,
      title: messages.step03,
      waitForEvent: {
        eventType: "click",
        selector: ".ant-dropdown-menu-submenu[data-item-id=email]",
      },
    },
    {
      id: "email-button-2",
      description: messages.step04Description,
      onBefore: async () => {
        // just letting the dropdown animation to complete
        return new Promise((resolve) => setTimeout(resolve, 100));
      },
      options: {
        disableInteraction: false,
        showBackground: true,
        showButtons: false,
      },
      position: "left",
      selector: ".ant-dropdown-menu-item[data-menu-id$=sendViaMMCEmail]",
      selectorFoundDelay: 100,
      title: messages.step04,
      waitForEvent: {
        eventType: "internal",
        selector: /^Clicked: .* -> Email from MMC$/i,
      },
    },
    {
      id: "email-button-3",
      onBefore: async () => {
        await new Promise<void>((resolve) =>
          waitForElement("[class*=mmc-js-modal-email]", resolve, {
            failureCallback: resolve,
            timeout: 1000,
          })
        );

        if (document.querySelector("[class*=mmc-js-modal-email-emails-missing]")) {
          return { action: "navigate", stepId: "email-modal-emails-missing" };
        }

        if (document.querySelector("[class*=mmc-js-modal-email-deals-selected]")) {
          return { action: "navigate", stepId: "email-modal-deals-selected" };
        }

        if (document.querySelector("[class*=mmc-js-modal-email-multiple-entities]")) {
          return { action: "navigate", stepId: "email-modal-multiple-entities" };
        }

        return { action: "navigate", stepId: "email-compose-subject" };
      },
    },
    {
      id: "email-modal-emails-missing",
      description: messages.step06Description,
      onAfter: async () => {
        return Promise.resolve({ action: "navigate", stepId: "email-compose-subject" });
      },
      options: {
        disableInteraction: false,
        showBackground: false,
        showButtons: false,
      },
      position: "bottom",
      selector: "[class*=mmc-js-modal-email-emails-missing] .ant-btn-primary",
      title: messages.step06,
      waitForEvent: {
        eventType: "click",
        selector: "[class*=mmc-js-modal-email-emails-missing] .ant-btn-primary",
      },
    },
    {
      id: "email-modal-deals-selected",
      description: messages.step09Description,
      onAfter: async () => {
        return Promise.resolve({ action: "navigate", stepId: "email-compose-subject" });
      },
      options: {
        disableInteraction: false,
        showBackground: false,
        showButtons: false,
      },
      position: "bottom",
      selector: "[class*=mmc-js-modal-email-deals-selected] .ant-btn-primary",
      title: messages.step09,
      waitForEvent: {
        eventType: "click",
        selector: "[class*=mmc-js-modal-email-deals-selected] .ant-btn-primary",
      },
    },
    {
      id: "email-modal-multiple-entities",
      description: messages.step07Description,
      onAfter: async () => {
        return Promise.resolve({ action: "navigate", stepId: "email-compose-subject" });
      },
      options: {
        disableInteraction: false,
        showBackground: false,
        showButtons: false,
      },
      position: "bottom",
      selector: "[class*=mmc-js-modal-email-multiple-entities] .ant-btn-primary",
      title: messages.step07,
      waitForEvent: {
        eventType: "click",
        selector: "[class*=mmc-js-modal-email-multiple-entities] .ant-btn-primary",
      },
    },
    {
      id: "email-compose-subject",
      canGoNext: () =>
        !!document.querySelector("[class*=EmailComposeModal_subject]:not(.mmc-js-empty)"),
      description: messages.step08Description,
      options: {
        disableInteraction: false,
        showBackground: true,
        showButtons: true,
      },
      position: "left",
      selector: "[class*=EmailComposeModal_subject]",
      title: messages.step08,
    },
    {
      id: "email-compose-body",
      canGoNext: () =>
        !!document.querySelector(
          "[class*=EmailComposeModal_body] [class*=editable][contenteditable]:not(.mmc-js-empty)"
        ),
      description: messages.step09Description,
      options: {
        disableInteraction: false,
        showBackground: true,
        showButtons: true,
      },
      position: "left",
      selector: "[class*=EmailComposeModal_body] [class*=editorContentWrapper]",
      title: messages.step09,
    },
    {
      id: "email-compose",
      description: messages.step10Description,
      options: {
        disableInteraction: false,
        showBackground: true,
        showButtons: false,
      },
      position: "left",
      selector: "[class*=EmailTemplateButton_button]",
      title: messages.step10,
      waitForEvent: {
        eventType: "click",
        selector: "[class*=EmailTemplateButton_button]",
      },
    },
    {
      id: "template-send",
      description: messages.step11Description,
      options: {
        disableInteraction: false,
        showBackground: true,
        showButtons: false,
      },
      position: "right",
      selector: "[data-menu-id$=saveNew]:not([aria-disabled=true])",
      selectorFoundDelay: 250,
      title: messages.step11,
      waitForEvent: {
        eventType: "click",
        selector: "[data-menu-id$=saveNew]:not([aria-disabled=true])",
      },
    },
    {
      id: "template-enter-name",
      description: messages.step12Description,
      options: {
        disableInteraction: false,
        showBackground: false,
        showButtons: false,
      },
      position: "bottom",
      selector: "[data-modal-id=saveNewTemplate] .ant-btn-primary",
      title: messages.step12,
      waitForEvent: {
        eventType: "internal",
        selector: "Clicked: Bulk Email Modal -> Save New Template",
      },
    },
    {
      id: "template-location",
      description: messages.step13Description,
      onAfter: async () => {
        const dropdown = document.querySelector(
          "[class*=EmailTemplateButton_dropdown]:not(.ant-dropdown-hidden)"
        );
        if (dropdown) {
          // if still visible, let's close it
          const element = document.querySelector("[class*=EmailTemplateButton_button]");
          if (element) {
            (element as HTMLElement).click();
          }
        }
      },
      onBefore: async () => {
        const selector = "[class*=EmailTemplateButton_button]";
        await new Promise<void>((resolve) => {
          waitForElement(selector, resolve);
        });
        const element = document.querySelector(selector);
        if (element) {
          (element as HTMLElement).click();
        }
        // and wait till dropdown animation ends
        await new Promise<void>((resolve) => {
          setTimeout(resolve, 250);
        });
      },
      selector: "[class*=EmailTemplateButton_dropdown]",
      title: messages.step13,
    },
    {
      id: "template-editing-1",
      canGoNext: () => !!document.querySelector("[data-action-id=update-template]"),
      description: messages.step14Description,
      options: {
        disableInteraction: false,
        showBackground: true,
        showButtons: true,
      },
      position: "left",
      selector: "[class*=EmailComposeModal_subject]",
      title: messages.step14,
    },
    {
      id: "template-editing-2",
      description: messages.step15Description,
      options: {
        disableInteraction: false,
        showBackground: true,
        showButtons: false,
      },
      position: "bottom",
      selector: "[data-action-id=update-template]",
      title: messages.step15,
      waitForEvent: {
        eventType: "click",
        selector: "[data-action-id=update-template]",
      },
    },
    {
      id: "completed",
      description: messages.step16Description,
      options: {
        disableInteraction: false,
        showBackground: false,
        showButtons: false,
      },
      selector: "[class*=EmailTemplateButton_updateConfirmation] .ant-btn-primary",
      selectorFoundDelay: 100,
      title: messages.step16,
      waitForEvent: {
        eventType: "click",
        selector: "[class*=EmailTemplateButton_updateConfirmation] .ant-btn-primary",
      },
    },
    {
      id: "completed",
      description: messages.step17Description,
      onAfter: closeComposeModal,
      title: messages.step17,
    },
  ],
};
